import { validateINN, validateKPP, validateOGRN } from '@/lib/inn';
import i18n from "@/plugins/i18n"

function required(short = false, errorMsg = '') {
  return [
    val => !!val || i18n.global.t(errorMsg) || (i18n.global.t(short ? 'Обязательное поле' : 'Это поле обязательно для заполнения'))
  ];
}

/**
 * Требуется текст или пустая строка, но не больше max символов.
 * @param {Number} max
 * @return {Array}
 */
function text(max) {
  return [
    val => !val || typeof val !== 'string' || val.length <= max || i18n.global.t('Количество символов превышает допустимое значение')
  ];
}

/**
 * Проверяет, что значение равно значению другого поля.
 * @param {String} string
 * @param {String} errorText
 * @return {Array}
 */
function equal(string, errorText) {
  return [
    val => !val || val === string || i18n.global.t(errorText) || i18n.global.t('Значения не совпадают')
  ];
}

/**
 * @returns {((function(*): (string|undefined))|*)[]}
 */
function email() {
  return [
    // https://stackoverflow.com/questions/201323/how-to-validate-an-email-address-using-a-regular-expression
    val => !val ||
    // eslint-disable-next-line
    /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/
      .test(val) || i18n.global.t('Некорректный адрес электронной почты')
  ];
}

function phone() {
  return [
    val => {
      if (!val) return true;
      if (typeof val === 'string' && val.trim().length > 255) return i18n.global.t('Количество символов превышает допустимое значение');
      if (typeof val === 'string' && val.trim().length < 1) return i18n.global.t('Некорректный номер телефона');
      return true;
    }
  ];
}

function inn() {
  return [
    val => !val || validateINN(val) || i18n.global.t('Некорректный ИНН')
  ];
}

function crn() {
  return [
    val => {
      if (!val) return true;
      if (val.length > 50) return 'CRN не должен превышать 50 символов';
      if (!/^[A-Za-z0-9\-\/. ]+$/.test(val)) return 'Некорректный CRN';
      return true;
    }
  ];
}

function kpp(contragent) {
  return [
    val => {
      const innLen = contragent.inn?.length;
      if (innLen === 12) {
        return true;
      }
      if (!val) {
        return i18n.global.t('Это поле обязательно для заполнения юридическим лицом');
      }
      return validateKPP(val) || i18n.global.t('Некорректный КПП (9 цифр)');
    }
  ];
}

function ogrn(contragent) {
  return [
    val => {
      const ogrnLen = contragent.inn?.length > 10 ? 15 : 13;
      if (!val || val.length !== ogrnLen) {
        return i18n.global.t(ogrnLen === 15 ? "ОГРНИП должен содержать 15 цифр" : "ОГРН должен содержать 13 цифр");
      }
      return validateOGRN(val, ogrnLen) || i18n.global.t('Некорректный ' + (ogrnLen === 15 ? 'ОГРНИП' : 'ОГРН'));
    }
  ];
}

function number(
    min, max, required = true, showMax = false, short = false,
    errorMax = '', errorMin = ''
) {
  return [
    val => {
      if (required && val === null) {
        return i18n.global.t(short ? 'Обязательное поле' : 'Это поле обязательно для заполнения');
      }
      if (val < min) {
        return i18n.global.t(
          errorMin ? errorMin : (short ? 'Мин. значение {_1}' : 'Минимальное допустимое значение {_1}'),
          {'_1': min.toString().replace('.', ',')}
        );
      }
      if (val > max) {
        return i18n.global.t(
          errorMax ? errorMax : (short ? 'Макс. ввод {_1}' : 'Вы превысили допустимое максимальное значение {_1}'),
          {'_1': showMax ? ` - ${max.toString().replace('.', ',')}` : ''}
        );
      }
      return true;
    }
  ];
}

function minValue(max) {
  return [
    val => {
      if (val >= max) return i18n.global.t('Мин. значение не должно превышать или быть равным макс. значению');
      return true;
    }
  ];
}

function maxValue(min) {
  return [
    val => {
      if (val <= min) return i18n.global.t('Макс. значение не должно быть меньше или равно мин. значению');
      return true;
    }
  ];
}

export default {
  required,
  equal,
  text,
  inn,
  crn,
  kpp,
  ogrn,
  email,
  phone,
  number,
  minValue,
  maxValue
};
